import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';
import { getLiveSocket } from '../../utils/liveSocket';

const POLL_MS = 60000; // fallback only; Socket.IO delivers changes instantly
const ADMIN_ROLES = ['principal', 'admin', 'admin1', 'admin2'];
const TEACHER_ROLES = ['teacher', 'teacher_admin_level1'];

export const OPEN_LIVE_PLAYER_EVENT = 'live-status:open-player';
export const ADMIN_STREAM_CONTROL_ID = 'admin-stream-control';
export const TEACHER_LIVE_SECTION_ID = 'teacher-live-section';

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

/**
 * Animated live-status pill for the top header.
 * - Students/Parents: GET /api/livestream/my-stream; click expands the global player.
 * - Principal/Admin: GET /api/livestream/all (any class live); click scrolls to <AdminStreamControl/>.
 * - Updates in real time via Socket.IO, with a silent 60s poll as fallback.
 * Props: user (optional, defaults to localStorage user), className, onClick (optional override).
 */
function LiveStatusBadge({ user, className = '', onClick }) {
  const stored = user || getStoredUser();
  const role = stored?.role;
  const userId = String(stored?._id || stored?.id || '');
  const isAdmin = ADMIN_ROLES.includes(role);
  const isTeacher = TEACHER_ROLES.includes(role);
  const eligible = !!userId && (isAdmin || isTeacher || role === 'student' || role === 'parent');

  const [isLive, setIsLive] = useState(false);
  const myClassId = useRef(null);

  const check = useCallback(async (signal) => {
    if (!eligible) return;
    try {
      const path = isAdmin ? 'all' : isTeacher ? 'active-streams' : 'my-stream';
      const url = `${API_BASE_URL}/api/livestream/${path}`;
      const res = await axios.get(url, { headers: { 'x-user-id': userId }, signal, timeout: 15000 });
      if (isAdmin) {
        setIsLive((res.data?.items || []).some((i) => i.isLive));
      } else if (isTeacher) {
        setIsLive(Array.isArray(res.data) && res.data.length > 0);
      } else {
        if (res.data?.classId) myClassId.current = String(res.data.classId);
        setIsLive(!!res.data?.isLive);
      }
    } catch {
      /* keep last known status on transient errors */
    }
  }, [eligible, isAdmin, isTeacher, userId]);

  useEffect(() => {
    if (!eligible) {
      setIsLive(false);
      return undefined;
    }
    const controller = new AbortController();
    check(controller.signal);
    const timer = setInterval(() => check(controller.signal), POLL_MS);

    const socket = getLiveSocket();
    const onChange = (payload) => {
      if (isAdmin || isTeacher || !myClassId.current || String(payload?.classId) === myClassId.current) check();
    };
    socket.on('liveStreamStarted', onChange);
    socket.on('liveStreamStopped', onChange);
    return () => {
      controller.abort();
      clearInterval(timer);
      socket.off('liveStreamStarted', onChange);
      socket.off('liveStreamStopped', onChange);
    };
  }, [eligible, isAdmin, isTeacher, check]);

  const handleClick = () => {
    if (onClick) return onClick(isLive);
    if (isAdmin || isTeacher) {
      const el = document.getElementById(isAdmin ? ADMIN_STREAM_CONTROL_ID : TEACHER_LIVE_SECTION_ID);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.dispatchEvent(new CustomEvent(OPEN_LIVE_PLAYER_EVENT));
    }
    return undefined;
  };

  if (!eligible) return null;

  const tone = isLive
    ? 'bg-green-100 text-green-700 ring-1 ring-green-300 hover:bg-green-200'
    : 'bg-gray-100 text-gray-500 hover:bg-gray-200';

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-live="polite"
      title={isAdmin ? 'Open live stream control' : isTeacher ? 'View live classes' : isLive ? 'Watch live class' : 'No live class right now'}
      className={`inline-flex min-h-[32px] items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold transition-colors ${tone} ${className}`}
    >
      {isLive ? (
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
        </span>
      ) : null}
      <span>{isLive ? '🟢 Live Class Running' : '⚪ No Live Class'}</span>
    </button>
  );
}

export default memo(LiveStatusBadge);
